<!-- 农业图层：地块多边形 + 气泡弹窗 + 取水/起飞点 -->
<template>
    <div>
        <LVector
            :zIndex="40"
            :defaultStyle="defaultStyle"
            :selectStyle="selectStyle"
            :selectAble="true"
            @selectFeature="handleSelectFeature"
        >
            <!-- 地块多边形 -->
            <LPolygonFeature
                v-for="field in fields"
                :key="'field-' + field.id"
                :id="String(field.id)"
                :positions="field.coordinates"
                :name="field.name"
                :styles="getFieldStyles(field)"
            />
            <!-- 取水点 -->
            <LPointFeature
                v-for="p in waterPoints"
                :key="'water-' + p.id"
                :id="String(p.id)"
                :longitude="p.lon"
                :latitude="p.lat"
                :name="p.name"
                :defaultStyle="waterStyle"
            />
            <!-- 起飞点 -->
            <LPointFeature
                v-for="p in takeoffPoints"
                :key="'takeoff-' + p.id"
                :id="String(p.id)"
                :longitude="p.lon"
                :latitude="p.lat"
                :name="p.name"
                :defaultStyle="takeoffStyle"
            />
        </LVector>
    </div>
</template>

<script>
import { Style, Fill, Stroke } from 'ol/style';
import { OMapMixin, LVector, LPointFeature, LPolygonFeature } from '@OpenLayersCommon';

/* ============================================================
 *  常量：样式颜色、作物图标、气泡布局配置
 * ============================================================ */
// 地块默认颜色（未识别作物时）
const DEFAULT_FIELD_FILL = 'rgba(91, 207, 74, 0.18)';
const DEFAULT_FIELD_STROKE = '#5bcf4a';

// 色板：按作物名哈希取色，同名稳定同色，相邻色板项色相差异大避免相邻地块撞色
// 设计原则：低饱和高明度，避开机库/起飞点等保留色（蓝青/橙）
const COLOR_PALETTE = [
    { fill: 'rgba(91, 207, 74, 0.28)', stroke: '#5bcf4a' }, // 翠绿
    { fill: 'rgba(73, 240, 253, 0.22)', stroke: '#49f0fd' }, // 青绿
    { fill: 'rgba(255, 217, 61, 0.25)', stroke: '#ffd93d' }, // 黄
    { fill: 'rgba(239, 130, 0, 0.25)', stroke: '#ef8200' }, // 橙
    { fill: 'rgba(125, 245, 176, 0.25)', stroke: '#7df5b0' }, // 亮绿
    { fill: 'rgba(167, 139, 250, 0.28)', stroke: '#a48bff' }, // 紫
    { fill: 'rgba(255, 107, 107, 0.28)', stroke: '#ff6b6b' }, // 红
    { fill: 'rgba(77, 150, 255, 0.25)', stroke: '#4d96ff' }, // 蓝
    { fill: 'rgba(107, 203, 119, 0.28)', stroke: '#6bcb77' }, // 草绿
    { fill: 'rgba(255, 168, 76, 0.25)', stroke: '#ffa84c' }, // 浅橙
    { fill: 'rgba(72, 200, 240, 0.25)', stroke: '#48c8f0' }, // 天蓝
    { fill: 'rgba(240, 168, 255, 0.25)', stroke: '#f0a8ff' }, // 粉
];

const WATER_COLOR = '#5bfffa';
const TAKEOFF_COLOR = '#ef8200';

/**
 * 按作物名计算哈希：与 Java String.hashCode 同算法，保证同名稳定哈希
 * 用于未在 CROP_COLOR_OVERRIDE 中出现的作物名，从 COLOR_PALETTE 取色
 */
function hashString(str) {
    let h = 0;
    if (!str || !str.length) return 0;
    for (let i = 0; i < str.length; i += 1) {
        h = ((h << 5) - h + str.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
}

/**
 * 取作物地块颜色：按名称哈希从 COLOR_PALETTE 取色（同名稳定）
 * 空名称回退默认绿
 */
function getCropColor(cropName) {
    if (!cropName) {
        return { fill: DEFAULT_FIELD_FILL, stroke: DEFAULT_FIELD_STROKE };
    }
    const idx = hashString(cropName) % COLOR_PALETTE.length;
    return COLOR_PALETTE[idx];
}

// 气泡整体布局配置（所有内边距、间距、字号、行高统一在此）
const BUBBLE_CONFIG = Object.freeze({
    maxBubbleWidth: 420, // 气泡最大宽度
    paddingX: 8, // 左侧内边距（图标贴左一点）
    paddingY: 8, // 上下内边距（缩小一点，整体更贴图标）
    minRightPad: 16, // 文字列右侧最小留白（动态框下，短文本右侧自动放大）
    minTextColW: 80, // 文字列最小宽度（保证短文本也宽松不挤压）
    iconSize: 50, // 图标直径
    iconGap: 0, // 图标与文字列之间的间距（紧凑一点）
    tipH: 10, // 底部倒三角高度
    radius: 8, // 气泡圆角
    title: { font: 'bold 16px sans-serif', lineHeight: 24 }, // 名称：放大一档
    body: { font: '14px sans-serif', lineHeight: 20 }, // 地址/面积：放大一档
    nameAreaGap: 0, // 名称与面积之间的间隔（无地址行时使用，紧贴 0）
    lineGap: 6, // 字段之间的额外垂直间距
    titleColor: '#1f3a5f',
    bodyColor: '#5b6b80',
    borderColor: '#5bcf4a', // 气泡边框：与地块描边统一为绿色（与机库蓝青区分）
    background: 'rgba(255, 255, 255, 0.96)',
    shadow: { color: 'rgba(20, 80, 30, 0.25)', blur: 8, offsetY: 2 },
    tipHalf: 8, // 倒三角底边半宽
    maxNameLines: 2, // 名称最多行数
    showAtZoom: 12, // 缩放等级 > 此值才显示气泡
});

/* ============================================================
 *  工具：纯函数
 * ============================================================ */

/**
 * 按最大宽度切分文本为多行；最后一行超出时用 '...' 截断
 */
function wrapText(ctx, text, maxWidth, maxLines) {
    if (!text) return [''];
    const lines = [];
    let current = '';
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        const test = current + ch;
        if (ctx.measureText(test).width > maxWidth) {
            if (lines.length === maxLines - 1) {
                // 最后一行：剩余内容用省略号截断
                let last = current;
                while (last.length && ctx.measureText(`${last}...`).width > maxWidth) {
                    last = last.slice(0, -1);
                }
                lines.push(`${last}...`);
                return lines;
            }
            lines.push(current);
            current = ch;
        } else {
            current = test;
        }
    }
    if (current) lines.push(current);
    return lines;
}

/**
 * 按最大宽度切分文本为多行，不省略
 */
function wrapTextNoEllipsis(ctx, text, maxWidth) {
    if (!text) return [];
    const lines = [];
    let current = '';
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        const test = current + ch;
        if (ctx.measureText(test).width > maxWidth && current) {
            lines.push(current);
            current = ch;
        } else {
            current = test;
        }
    }
    if (current) lines.push(current);
    return lines;
}

/**
 * 气泡路径：圆角矩形 + 底部倒三角（连续一体，无内边线）
 */
function traceBubblePath(ctx, x, y, w, h, r, tipX, tipY, half) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(tipX + half, y + h);
    ctx.lineTo(tipX, tipY);
    ctx.lineTo(tipX - half, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

/**
 * 取数组中测量文字宽度的最大值
 */
function maxLineWidth(ctx, lines) {
    return lines.reduce((max, line) => Math.max(max, ctx.measureText(line).width), 0);
}

/**
 * 加载图片，自动处理跨域
 */
function loadImage(src) {
    const img = new Image();
    if (/^https?:\/\//i.test(src)) img.crossOrigin = 'anonymous';
    img.src = src;
    return img;
}

/* ============================================================
 *  气泡布局：纯计算，返回所有需要的尺寸与位置
 * ============================================================ */

/**
 * 计算气泡的文本行与几何布局
 * @returns {{
 *   nameLines: string[], addressLines: string[], areaText: string,
 *   boxW: number, boxH: number, left: number, top: number,
 *   iconCx: number, iconCy: number, textX: number,
 *   textTop: number, textBottom: number,
 * }}
 */
function computeBubbleLayout(ctx, field, cfg, options = {}) {
    const { showAddress = true } = options;
    const { paddingX, paddingY, iconSize, iconGap, maxBubbleWidth,
        title, body, lineGap, nameAreaGap, maxNameLines, minTextColW, minRightPad } = cfg;

    const areaText = options.areaText !== undefined ? options.areaText : `面积：${field.area ?? 0}亩`;
    const addressText = (showAddress && field.address)
        ? (options.addressText !== undefined ? options.addressText : `地址：${field.address}`)
        : '';

    // 文字列可用宽度 = 气泡上限宽 - 左侧paddingX - 图标 - iconGap
    // 注意：右侧不预先扣 minRightPad，而是用"先按文字列测量，再用 minRightPad 兜底"的策略
    // 让短文本与长文本都能得到恰到好处的右侧边距
    const textMaxWidth = maxBubbleWidth - paddingX - iconSize - iconGap;

    // 标题断行（带省略号）
    ctx.font = title.font;
    const nameLines = wrapText(ctx, field.name || '', textMaxWidth, maxNameLines);

    // 地址断行（不带省略号）。showAddress=false 时直接空数组，不参与布局与绘制
    ctx.font = body.font;
    const addressLines = addressText ? wrapTextNoEllipsis(ctx, addressText, textMaxWidth) : [];

    // 文字列最大宽 = 标题/面积/地址 三者取大，但不少于 minTextColW（短文本也保持宽松）
    // 注意：nameLines 必须切回 title.font 测量——上方字体已切到 body.font（14px），
    // 直接测会把 16px bold 标题宽度低估约 15%，导致长标题贴气泡右边框
    ctx.font = title.font;
    const nameColW = maxLineWidth(ctx, nameLines);
    ctx.font = body.font;
    const areaWidth = ctx.measureText(areaText).width;
    const measuredColW = Math.max(nameColW, areaWidth, maxLineWidth(ctx, addressLines));
    const textColW = Math.max(measuredColW, minTextColW);

    // 气泡整体宽 = 左侧paddingX + 图标 + iconGap + 文字列最大宽 + 右侧动态留白
    // 右侧留白 = 真实测得文字列宽（≤ textMaxWidth - 文字列内容宽）+ minRightPad 兜底
    // 合并为 boxW 上限 = maxBubbleWidth；下限 = 让 boxW 至少能装下列宽 + 两侧 padding
    const iconColW = paddingX + iconSize + iconGap; // 图标列（含与文字列的间距）
    const leftPad = paddingX; // 左侧与右侧对称：保持视觉一致
    const minBoxW = leftPad + iconColW + textColW + minRightPad;
    // 在 [minBoxW, maxBubbleWidth] 范围内，气泡宽度取"撑满测量值"，
    // 这样长文字列直接触发 maxBubbleWidth 上限，剩余空间全部作为右侧 padding
    const boxW = Math.min(maxBubbleWidth, Math.max(minBoxW, iconColW + leftPad + measuredColW + minRightPad));

    // 名称到面积之间的间隔：与 drawBubbleText 中的 nameToAreaGap 保持完全一致，
    // 否则绘制时真实行高会超出 boxH 底部圆角范围
    const hasAddress = addressLines.length > 0;
    const nameToAreaGap = hasAddress ? lineGap : nameAreaGap;

    // 高度 = 上下内边距 + 名称N行 + 字段间距 + 地址extraLines行 + 名称到面积间隔 + 面积1行
    const boxH = paddingY
        + nameLines.length * title.lineHeight
        + (hasAddress ? lineGap + addressLines.length * body.lineHeight : 0)
        + nameToAreaGap
        + body.lineHeight
        + paddingY;

    return {
        nameLines,
        addressLines,
        areaText,
        boxW,
        boxH,
        textMaxWidth,
    };
}

/**
 * 根据布局结果与锚点 (x, y)，推导所有渲染坐标
 */
function resolveBubbleGeometry(layout, x, y, cfg) {
    const { paddingX, paddingY, iconSize, tipH } = cfg;
    const { boxW, boxH, nameLines, addressLines } = layout;
    const { title, body, lineGap, nameAreaGap, iconGap } = cfg;

    const left = x - boxW / 2;
    const top = y - boxH - tipH;

    // 名称到面积的间隔必须与 computeBubbleLayout 完全一致，
    // 否则 iconCy 算偏（图标垂直居中到文字块中心，文字块必须 = boxH 的文字部分）
    const hasAddress = addressLines.length > 0;
    const nameToAreaGap = hasAddress ? lineGap : nameAreaGap;

    // 文字块从 top+paddingY 开始，按渲染顺序累加高度
    const textTop = top + paddingY;
    let textBottom = textTop + nameLines.length * title.lineHeight;
    if (hasAddress) {
        textBottom += lineGap + addressLines.length * body.lineHeight;
    }
    textBottom += nameToAreaGap + body.lineHeight; // 面积行

    const iconCx = left + paddingX + iconSize / 2;
    const iconCy = (textTop + textBottom) / 2; // 图标居中到文字块中心
    const textX = left + paddingX + iconSize + iconGap;

    return { left, top, textTop, textBottom, iconCx, iconCy, textX, boxW, boxH };
}

/* ============================================================
 *  气泡绘制：纯渲染
 * ============================================================ */

function drawBubbleBackground(ctx, geom, cfg, anchorX) {
    const { left, top, boxW, boxH } = geom;
    const { radius, tipH, background, borderColor, shadow, tipHalf } = cfg;
    const tipX = anchorX; // 倒三角尖端指向地块中心
    const tipY = top + boxH + tipH;
    const pathArgs = [ctx, left, top, boxW, boxH, radius, tipX, tipY, tipHalf];

    // 阴影 + 填充
    ctx.shadowColor = shadow.color;
    ctx.shadowBlur = shadow.blur;
    ctx.shadowOffsetY = shadow.offsetY;
    ctx.fillStyle = background;
    traceBubblePath(...pathArgs);
    ctx.fill();
    // 清阴影后再描边，避免描边也带阴影
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 1;
    traceBubblePath(...pathArgs);
    ctx.stroke();
}

function drawCropIcon(ctx, iconCx, iconCy, img, cfg) {
    const draw = cfg.iconSize - 10; // 内圈直径
    // 外圈描边
    ctx.strokeStyle = '#5bcf4a';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(iconCx, iconCy, draw / 2, 0, Math.PI * 2);
    ctx.stroke();
    // 图片未加载完成时直接返回
    if (!img.complete || !img.naturalWidth) return;
    // 圆形裁剪后绘制图片
    ctx.save();
    ctx.beginPath();
    ctx.arc(iconCx, iconCy, draw / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, iconCx - draw / 2, iconCy - draw / 2, draw, draw);
    ctx.restore();
}

/**
 * 绘制气泡内文字：标题多行 + 地址多行 + 面积一行
 * 文字块从 textY 开始，按字段间 lineGap 分段
 */
function drawBubbleText(ctx, geom, layout, cfg) {
    const { textX, textTop } = geom;
    const { nameLines, addressLines, areaText } = layout;
    const { title, body, lineGap, nameAreaGap, titleColor, bodyColor } = cfg;

    // 名称与面积之间的间隔：仅有"标题+面积"两行时用更紧凑的 nameAreaGap，
    // 中间有地址行时仍走常规 lineGap，避免地址与面积过近。
    const nameToAreaGap = addressLines.length ? lineGap : nameAreaGap;

    ctx.textBaseline = 'top';
    let y = textTop;
    // 标题
    ctx.fillStyle = titleColor;
    ctx.font = title.font;
    for (const line of nameLines) {
        ctx.fillText(line, textX, y);
        y += title.lineHeight;
    }
    // 地址
    if (addressLines.length) {
        y += lineGap;
        ctx.fillStyle = bodyColor;
        ctx.font = body.font;
        for (const line of addressLines) {
            ctx.fillText(line, textX, y);
            y += body.lineHeight;
        }
    }
    // 面积
    y += nameToAreaGap;
    ctx.fillStyle = bodyColor;
    ctx.font = body.font;
    ctx.fillText(areaText, textX, y);
}

/* ============================================================
 *  组件
 * ============================================================ */
export default {
    name: 'MapLayer',
    mixins: [OMapMixin],
    components: { LVector, LPointFeature, LPolygonFeature },
    props: {
        fields: { type: Array, default: () => [] },
        waterPoints: { type: Array, default: () => [] },
        takeoffPoints: { type: Array, default: () => [] },
        // 是否显示地址行（true 显示，false 隐藏）
        showAddress: { type: Boolean, default: true },
    },
    data() {
        return {
            currentZoom: 0,
            // 默认样式：未识别作物时使用；具体每个地块的颜色由 getFieldStyles 按作物类型动态生成
            defaultStyle: {
                fill: { color: DEFAULT_FIELD_FILL },
                stroke: { color: DEFAULT_FIELD_STROKE, width: 2 },
            },
            selectStyle: {
                fill: { color: 'rgba(91, 207, 74, 0.4)' }, // 选中态：绿色高亮（与默认色同族但加深）
                stroke: { color: '#7df5b0', width: 3 },
            },
        };
    },
    computed: {
        waterStyle() {
            return {
                image: {
                    type: 'circle',
                    radius: 12,
                    fill: { color: WATER_COLOR },
                    stroke: { color: '#fff', width: 2 },
                },
                text: { text: this.$t('nongye.mapLayer.waterChar'), font: 'bold 12px sans-serif', fill: { color: '#fff' } },
            };
        },
        takeoffStyle() {
            return {
                image: {
                    type: 'circle',
                    radius: 12,
                    fill: { color: TAKEOFF_COLOR },
                    stroke: { color: '#fff', width: 2 },
                },
                text: { text: this.$t('nongye.mapLayer.takeoffChar'), font: 'bold 12px sans-serif', fill: { color: '#fff' } },
            };
        },
    },
    async mounted() {
        try {
            const map = await this.getMap();
            this.olMap = map;
            const view = map && map.getView && map.getView();
            if (view) {
                this.currentZoom = view.getZoom() || 0;
                this.handleZoomChange = () => {
                    this.currentZoom = view.getZoom() || 0;
                };
                view.on('change:resolution', this.handleZoomChange);
            }
        } catch (e) {
            // 拿不到 map 时不阻断
            console.warn('[MapLayer] getMap failed', e);
        }
    },
    beforeDestroy() {
        if (this.olMap && this.handleZoomChange) {
            const view = this.olMap.getView && this.olMap.getView();
            view && view.un && view.un('change:resolution', this.handleZoomChange);
        }
    },
    methods: {
        /**
         * 地块样式：返回 OL Style 数组
         *  - [0] 多边形描边 + 填充（按作物类型着色）
         *  - [1] 整体气泡（绿色圆形图标 + 地块名 + 地址 + 面积），用 renderer canvas 自绘
         */
        getFieldStyles(field) {
            const self = this;
            // 按作物名动态取色：推荐色 override -> 哈希色板 -> 默认绿
            const cropColor = getCropColor(field.cropName);
            // 气泡边框使用同色系，让气泡与地块保持视觉一致
            const cfg = { ...BUBBLE_CONFIG, borderColor: cropColor.stroke };
            // 接口保证每个地块都有 cropImgUrl；loadImage 内部对 http(s) 自动处理跨域
            const cropImg = loadImage(field.cropImgUrl);

            const renderer = (coordinates, state) => {
                const ctx = state.context;
                if (self.currentZoom <= cfg.showAtZoom) return;
                const pt = Array.isArray(coordinates) ? coordinates : coordinates && coordinates[0];
                if (!pt || pt.length < 2) return;
                const x = pt[0];
                const y = pt[1];

                // 1) 算布局：文本行 + 气泡尺寸
                const layout = computeBubbleLayout(ctx, field, cfg, {
                    showAddress: self.showAddress,
                    areaText: self.$t('nongye.mapLayer.area', { area: field.area ?? 0 }),
                    addressText: field.address ? self.$t('nongye.mapLayer.address', { address: field.address }) : '',
                });
                // 2) 算几何：所有位置坐标
                const geom = resolveBubbleGeometry(layout, x, y, cfg);
                // 3) 渲染：背景 -> 图标 -> 文字
                drawBubbleBackground(ctx, geom, cfg, x);
                drawCropIcon(ctx, geom.iconCx, geom.iconCy, cropImg, cfg);
                drawBubbleText(ctx, geom, layout, cfg);
            };

            return [
                new Style({
                    fill: new Fill({ color: cropColor.fill }),
                    stroke: new Stroke({ color: cropColor.stroke, width: 2 }),
                }),
                new Style({
                    geometry: (feat) => {
                        const g = feat.getGeometry();
                        return g && g.getInteriorPoint ? g.getInteriorPoint() : null;
                    },
                    renderer,
                }),
            ];
        },
        handleSelectFeature(feature) {
            if (!feature) return;
            const id = feature.getId();
            if (!id) return;
            const field = this.fields.find((f) => String(f.id) === String(id));
            if (field) {
                this.$emit('field-click', field);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
/* 图层组件本身不渲染 DOM */
</style>
